import React, { useCallback, useState, useEffect, use } from 'react';
import { useSelector } from 'react-redux';
import NeedsPage from 'src/pages/needs';
import * as needFiltersApi from 'src/apis/needFilters';
import * as etlApi from "src/apis/etl";
import * as needApi from 'src/apis/need';
import * as deallocationApi from 'src/apis/deallocation';
import * as positionMapApi from 'src/apis/positionMaps';
import * as fitApi from 'src/apis/fit';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * NeedsContainer component - Need service layer to call API functions and send fetched data to main page component.
 * @returns {JSX.Element}
 * @example
 *  <NeedsContainer />
 */
function NeedsContainer() {
    const sessionUser = useSelector((state) => state.login.sessionUser);
    const [listFetching, setListFetching] = useState(false);
    const [needs, setNeeds] = useState([]);
    const [filters, setFilters] = useState([]);
    const [etl, setEtl] = useState('');
    const [openDeallocations, setOpenDeallocations] = useState([]);
    const [matchDeallocations, setMatchDeallocations] = useState([]);
    const [positionMaps, setPositionMaps] = useState([]);
    const [totalCount, setTotalCount] = useState([]);

    const { t } = useTranslation();

   /**
 * Get open and match deallocations with necessary attributes only.
 */
    const getDeallocations = async (queryParams = {}, paginationOptions = {}) => {
        try {
            const response = await deallocationApi.listForNeeds(queryParams, paginationOptions);
            const { data } = response;
            const { result } = data;
            if(result) {
                setOpenDeallocations(result.openedDeallocations);
                setMatchDeallocations(result.matchDeallocations);
                setTotalCount(result.totalCount)
            }
        } catch(err) {
            setOpenDeallocations([]);
            setMatchDeallocations([]);
            console.log(err);
        };
    }
    /**
     * Get list of existing position maps.
     */
    const getPositionMaps = async () => {
        try {
            const response = await positionMapApi.getPositionMaps();
            const { data } = response;
            const { result } = data;
            if(result) {
                setPositionMaps(result);
            }
        } catch(err) { 
            setPositionMaps([]);
            // notification.error({message: t("errors.needs.getData")});
            console.log(err);
        };
    }

    /**
     * Get current ETL update datetime.
     */
    const getEtlDate = async () => {
        try {
            const response = await etlApi.getETL();
            const { data } = response;
            const { result } = data;
            if(result.updateTimetable) {
                setEtl(result.updateTimetable);
            }
        } catch(err) { 
            setEtl('');
            // notification.error({message: t("errors.needs.getData")});
            console.log(err);
        };
    }

    /**
     * Get existing filters for Needs.
     */
    const getFilters = useCallback(async () => {
        setListFetching(true);
        try {
            const response = await needFiltersApi.list();
            const { data } = response;
            const { result } = data;
            for (let i = 0; i < result.interviewRequired.length; i++) {
                if (result.interviewRequired[i] === 'Y') result.interviewRequired[i] = { value: 'Y', label: 'Yes'};
                else result.interviewRequired[i] = { value: 'N', label: 'No'};
            }
            setFilters(result);
        } catch (e) {
            // notification.error({message: t("errors.needs.getData")});
        }
        setListFetching(false);
    }, [t]);

    /**
     * Get all needs with optional filters.
     */
    const filterNeeds = useCallback(async (filters = {}) => {
        setListFetching(true);
        try {
            const response = await needApi.list(filters);
            const { data } = response;
            const { result } = data;
            setNeeds(result);
        } catch (e) {
            // notification.error({message: t("errors.needs.getData")});
        }
        setListFetching(false);
    }, [t]);

    useEffect(() => {
        /**
         * Fetch all data upon changes.
         */
        async function fetchInitData() {
            await Promise.all([getFilters(), getEtlDate(), getPositionMaps()]);
        }

        fetchInitData();
    }, [filterNeeds, getFilters]);

    /**
     * Call API method for canceling fit.
     * @param {string} fitId - Text with fitId to be canceled
     * @returns {Promise<Object>}
     */
    const handleCancelFit = (fitId, cancelCategory, cancelSubcategory) => {
        return fitApi.cancelFit(fitId, cancelCategory, cancelSubcategory);
    }

    /**
     * Call API method for creating fit.
     * @param {Array<string>} deallocationsIds - Array with deallocation Ids to create a fit (up to three)
     * @param {Array<string>} needId - Array with needId to create a fit
     * @returns {Promise<Object>}
     */
    const handleCreateFit = (deallocationsIds, needId) => {
        return fitApi.requestFit(deallocationsIds, needId);
    }

    /**
     * Call API method for creating a match on a fit.
     * @param {Object} fitMatch - Object with necessary attributes to create a match
     * @returns {Promise<Object>}
     */
    const handleCreateMatch = (fitMatch) => {
        return fitApi.fitMatchPut(fitMatch);
    }

    /**
     * Call API method for canceling a match.
     * @param {Object} rollbackItem - Object with necessary attributes to cancel a match
     * @returns {Promise<Object>}
     */
    const handleRollbackMatch = (rollbackItem) => {
        return fitApi.rollbackMatch(rollbackItem);
    }

    return (
        <NeedsPage
            user={sessionUser}
            filters={filters}
            needs={needs}
            filterNeeds={filterNeeds}
            listFetching={listFetching}
            etlDate={etl}
            onCancelFit={handleCancelFit}
            onCreateFit={handleCreateFit}
            onCreateMatch={handleCreateMatch}
            onRollbackMatch={handleRollbackMatch}
            fitDeallocations={openDeallocations}
            matchDeallocations={matchDeallocations}
            getDeallocations={getDeallocations}
            positionMaps={positionMaps}
        />
    );
}

export default NeedsContainer;
